import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useContext from '../../../../../hooks/useContext';
import useHistory from '../../../../../Router/useHistory';
import { ProductContext } from '../ProductProvider';

const getInitialSelection = (configuration?: { [key: string]: string }): ConfigSelection => {
  if (!configuration) {
    return undefined;
  }
  const selection: ConfigSelection = {};
  return Object.keys(configuration).reduce(
    (acc, type) => ({
      ...acc,
      [type]: configuration[type],
    }),
    selection,
  );
};

interface IKeyValue {
  key: string;
  value: string;
}

interface ISelectConfigOptionItem {
  contiguous?: IOptionResponse;
  variant?: IOptionResponse;
}

export interface IConfigOptionItem extends ISelectConfigOptionItem {
  disabledMessage?: {
    targetOption: IKeyValue;
    from: IKeyValue[];
    to: IKeyValue[];
  };
  oldPriceDiff: number;
  option: IConfigurationItem;
  priceDiff: number;
}

interface IConfigurationContext {
  selection: ConfigSelection;
  selectOption: (item: ISelectConfigOptionItem) => void;
}

export const ConfigurationContext = createContext<IConfigurationContext | undefined>(undefined);

interface IConfigurationProvider {
  children: ReactNode;
  configuration: ConfigSelection;
}

const ConfigurationProvider = ({ children, configuration }: IConfigurationProvider) => {
  const { location, replace } = useHistory();
  const [selection, setSelection] = useState(() => getInitialSelection(configuration));

  const { setConfigurationId, setM3Id, setVariantId, setGroupId, updateParsedProduct } =
    useContext(ProductContext);

  useEffect(() => {
    setSelection(getInitialSelection(configuration));
  }, [configuration]);

  const selectOption = useCallback(
    (item: ISelectConfigOptionItem) => {
      const variant = item.variant || item.contiguous;

      const configurations = variant?.configurations;

      if (variant && configurations) {
        const nextSelection = (Object.keys(configurations) as ConfigurationType[]).reduce(
          (acc, key) => ({ ...acc, [key]: configurations[key].id }),
          {} as ConfigSelection,
        );

        setSelection(nextSelection);
        setConfigurationId(variant.configurationId);
        setM3Id(variant.m3Id);
        setVariantId(variant.variantId);
        setGroupId(variant.groupId);
        updateParsedProduct(variant);
      }
    },
    [updateParsedProduct],
  );

  const queryString =
    (selection &&
      Object.keys(selection)
        .map((option) => `ck_${option}=${selection[option as ConfigurationType]}`)
        .join('&')) ||
    '';

  useEffect(() => {
    if (queryString) {
      replace(`${location.pathname}?${queryString}`);
    }
  }, [queryString]);

  const context = useMemo(
    () => ({
      selection,
      selectOption,
    }),
    [selection],
  );

  return <ConfigurationContext.Provider value={context}>{children}</ConfigurationContext.Provider>;
};

export default ConfigurationProvider;
